import {
  faCube,
  faQuestion,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import React, { useMemo } from "react";

import {
  PropertiesList,
  PropertiesListItem,
} from "src/base-components/PropertiesList";
import { CaseBlock, CaseBlockProps } from "src/cases/caseView/CaseBlock";
import {
  CaseEnvironment,
  EntityDetailsCaseBlockConfigAttributes,
} from "src/clients/case-management";
import { TruncatedIdPill } from "src/decisionsOverview/TruncatedIdPill";
import { EmptyState } from "src/design-system/EmptyState";
import { renderValue } from "src/entities/entityView/EntityDetailsSidePane";
import { findSchema, getTableData } from "src/entities/entityView/utils";
import {
  useEntity,
  useEntitySchemas,
  EntitySchemaProperty,
  Cardinality,
} from "src/entities/queries";
import { RelationIcon } from "src/eventsCatalogue/SchemaEditor/RelationIcons";
import { PROPERTY_TYPES_TO_ICONS } from "src/eventsCatalogue/SchemaEditor/utils";
import { Environment } from "src/hooks/useEnvironment";
import { useWorkspaceContext } from "src/router/routerContextHooks";

type EntityViewerBlockProps = Omit<CaseBlockProps, "children"> & {
  data: EntityDetailsCaseBlockConfigAttributes;
  caseEnv?: CaseEnvironment;
};

export const EntityDetailsBlock: React.FC<EntityViewerBlockProps> = ({
  data,
  caseEnv,
  ...props
}) => {
  const entityId = data.entity.id;
  const { workspace } = useWorkspaceContext();

  const env = caseEnv === CaseEnvironment.LIVE ? "live" : "sandbox";

  const { data: entitySchemas, isLoading: isLoadingSchemas } = useEntitySchemas(
    {
      baseUrl: workspace.base_url ?? "",
    },
  );

  const { data: entityData, isLoading: isLoadingEntity } = useEntity({
    schema: data.entity.type,
    env,
    entityId,
    enabled: !isLoadingSchemas && !!data.entity.type && !!data.entity.id,
  });

  const [tableData, entitySchema] = useMemo(() => {
    const entitySchema = findSchema(data.entity.type, entitySchemas);
    return [getTableData(entitySchema, entityData), entitySchema];
  }, [data.entity.type, entityData, entitySchemas]);

  const items: PropertiesListItem[] = useMemo(() => {
    if (!tableData || !entitySchema || !entityData) return [];

    return tableData.map((row) => {
      const isRelation = row._type === "relation";

      const icon = isRelation ? (
        <RelationIcon
          cardinality={(row._cardinality as Cardinality) ?? Cardinality.ONE}
          size="2xs"
        />
      ) : (
        (PROPERTY_TYPES_TO_ICONS[
          row._type as keyof typeof PROPERTY_TYPES_TO_ICONS
        ] ?? faQuestion)
      );

      const value = row.isId ? (
        <TruncatedIdPill id={row.value as string} />
      ) : (
        renderValue({
          property: row as EntitySchemaProperty,
          propertyValue: row.value,
          isWindowPill: false,
          foreginKeyToPrimaryProperty: entityData._fk_pp || {},
          maxDisplayLines: 3,
          environment: caseEnv as Environment,
        })
      );

      return {
        key: row._property_key,
        label: row._display_name || row._property_key,
        value,
        icon,
      };
    });
  }, [tableData, entitySchema, entityData, caseEnv]);

  const isLoading = isLoadingSchemas || isLoadingEntity;

  if (!entityId) {
    return (
      <CaseBlock {...props} isLoading={false}>
        <EmptyState
          dataLoc="entity-details-no-entity-id"
          description="This block currently has no linked entity."
          headline="No entity provided"
          icon={faSearch}
          size="sm"
        />
      </CaseBlock>
    );
  }

  if (!isLoading && !entityData) {
    return (
      <CaseBlock {...props} isLoading={false}>
        <EmptyState
          description="Unable to load entity data"
          headline="Error loading entity"
          icon={faCube}
          size="sm"
          variant="error"
        />
      </CaseBlock>
    );
  }

  if (!isLoading && items.length === 0) {
    return (
      <CaseBlock {...props} isLoading={false}>
        <EmptyState
          description="This entity has no data to display"
          headline="No data available"
          icon={faCube}
          size="sm"
        />
      </CaseBlock>
    );
  }

  return (
    <CaseBlock {...props} isLoading={isLoading}>
      <PropertiesList
        dataLocPrefix="entity-viewer-block"
        isLoading={isLoading}
        items={items}
        showIcons={true}
      />
    </CaseBlock>
  );
};
